import { useState, useMemo, useEffect } from 'react';
import { useParams, useSearch, useRouter, Navigate } from '@tanstack/react-router';
import { useTenant, useExtensions, useCdrStats, useTenants } from '../../lib/api/hooks.js';
import { useTrunkStatus } from '../../lib/api/hooks-products.js';
import {
  useRingGroups, useQueues, useIvrs, useNumbers, useTenantUsers,
} from '../../lib/api/hooks-tms.js';
import { Icon, BrandMark } from '../../components/ui/Icon.js';
import { ThemeToggle } from '../../components/layout/ThemeToggle.js';
import { StatusBadge, ErrorBanner, Stat, Empty } from '../../components/ui/Primitives.js';
import { useSession, useOwnOnly } from '../../lib/auth/session.js';
import { formatMoney, formatNumber } from '../../lib/format.js';
import { CONFIG_NAV, groupFor } from './config-nav.js';
import { useSubroute, childSubroute, SubrouteProvider } from '../../lib/subroute.js';

import { ExtensionsPage } from '../extensions/ExtensionsPage.js';
import { QueueReportTab } from './QueueReportTab.js';
import { CdrsPage } from '../cdrs/CdrsPage.js';
import { BillingPage } from '../billing/BillingPage.js';
import { AccountTrunksTab } from '../accounts/AccountTrunksTab.js';
import { TrunkStatusTab } from './TrunkStatusTab.js';
import { OwnTrunksPanel } from '../carriers/OwnTrunksPanel.js';
import { RingGroupsTab } from './RingGroupsTab.js';
import { QueuesTab } from './QueuesTab.js';
import { ConferenceRoomsTab } from './ConferenceRoomsTab.js';
import { ParkingLotsTab } from './ParkingLotsTab.js';
import { IvrsTab } from './IvrsTab.js';
import { SchedulesTab } from './SchedulesTab.js';
import { DepartmentsTab } from './DepartmentsTab.js';
import { ProvisioningTab } from '../provisioning/ProvisioningTab.js';
import { TemplatesPage } from '../provisioning/TemplatesPage.js';
import { RecordingTab } from './RecordingTab.js';
import { DialPolicyTab } from './DialPolicyTab.js';
import { AuditTab } from './AuditTab.js';
import { VoicemailTab } from './VoicemailTab.js';
import { MediaTab } from './MediaTab.js';
import { NumbersTab } from './NumbersTab.js';
import { InboundRoutesTab } from './InboundRoutesTab.js';
import { UsersTab } from './UsersTab.js';
import { ApiKeysTab } from './ApiKeysTab.js';
import { OutboundRoutesTab } from './OutboundRoutesTab.js';
import { ScreeningTab } from './ScreeningTab.js';
import { AgentsTab } from './AgentsTab.js';
import { ContactsPage } from '../phonebook/ContactsPage.js';
import { CompaniesPage } from '../phonebook/CompaniesPage.js';
import { FeatureCodesTab } from './FeatureCodesTab.js';
import { UsageTab } from './UsageTab.js';
import { AccountDashboard } from './AccountDashboard.js';

/**
 * Full account configuration, in its own browser tab.
 *
 * Its own SIDEBAR rather than the console's: this is a working session on one
 * customer with a deep feature tree, and a top tab strip cannot carry it
 * without either truncating or wrapping. The customer is named in the header
 * and in the URL, so with three of these open there is never a question of
 * which account is being edited.
 */
export function ManageAccountPage() {
  // The account's management screens are not a self-service user's (useOwnOnly).
  if (useOwnOnly()) return <Navigate to="/my/extensions" replace />;
  return <ManageAccount />;
}

function ManageAccount() {
  /**
   * The URL carries the account's SLUG, not its uuid.
   *
   * `/manage/11111111-1111-4111-8111-111111111111?section=billing` is 58
   * characters of which 36 are a uuid nobody can read, check or say out loud --
   * and this is the link support pastes into a ticket. `/manage/meridian` is
   * eight, and it says which customer.
   *
   * A uuid is still accepted, so every link already sent out keeps working and
   * the two live callers that navigate here by id (the realtime pages) need no
   * change. That back-compatibility is why this resolves rather than validates.
   */
  const { tenantId: ref } = useParams({ strict: false }) as { tenantId: string };
  const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(ref);
  // Only fetched when the ref is a slug; by uuid there is nothing to look up.
  const directory = useTenants({ limit: 200 });
  const tenantId = isUuid
    ? ref
    : directory.data?.data.find((t) => t.slug === ref)?.id ?? '';
  /**
   * The open section lives in the URL, not in component state -- as a path,
   * /manage/meridian/extensions, not ?section=extensions (14 September 2026).
   *
   * Doc 07 says every view encodes its state in the URL so support can ask for
   * a link and see exactly what the customer sees. This screen did not: it held
   * the section in useState, so every link to it opened on the dashboard no
   * matter what the sender was looking at, and the platform Live view could not
   * deep-link into a seat at all.
   */
  const route = useSubroute();
  const router = useRouter();
  // Links from before sections were paths: ?section=extensions&ext=<id>&view=phone.
  const legacy = useSearch({ strict: false }) as { section?: string; ext?: string; view?: string };
  const section = route.rest[0] ?? legacy.section ?? 'dashboard';
  const can = useSession((s) => s.can);
  const [open, setOpen] = useState<Set<string>>(() => {
    const initial = new Set(['overview', 'people', 'routing']);
    const g = groupFor(section);
    if (g) initial.add(g);
    return initial;
  });

  const account = useTenant(tenantId);
  /**
   * A slug that matches no account. Without this the page waits on a query that
   * is disabled and never resolves -- a spinner that means "wrong link" and
   * does not say so.
   */
  const unknownSlug = !isUuid && directory.isSuccess && !tenantId;
  const t = account.data;

  // The client's TMS, whoever has it open (owner, 18 September 2026).
  const brandName = useSession((s) => s.session?.brand?.name) ?? 'ArcNet';
  useEffect(() => { document.title = `${brandName} - TMS`; }, [brandName]);

  /**
   * Old links are rewritten to the path they mean, so none of them dead-ends,
   * and an account opened by id -- the realtime pages link that way -- is
   * rewritten to its slug once it has loaded, as /accounts does. Either way the
   * address bar ends up with the short one.
   */
  const rest = route.rest.join('/');
  useEffect(() => {
    if (!legacy.section) return;
    route.go([legacy.section,
      ...(legacy.ext ? [legacy.ext, ...(legacy.view ? [legacy.view] : [])] : [])], { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legacy.section, legacy.ext, legacy.view]);
  useEffect(() => {
    if (isUuid && t?.slug) router.history.replace(`/manage/${t.slug}${rest ? `/${rest}` : ''}`);
  }, [isUuid, t?.slug, rest, router]);

  // Counts shown as pills. Cheap because every one of these is already
  // fetched by a tab the operator is likely to open anyway.
  // Only what the role may read: a count the API refuses is a 403 in the log for every page view, and a
  // menu entry it belongs to is hidden anyway (permission sweep, 19 September 2026).
  const readable = (perm: string): string => (can(perm) ? tenantId : '');
  const counts = {
    extensions: useExtensions(readable('extension:read') || null, { limit: 200 }).data?.data.length,
    ringGroups: useRingGroups(readable('extension:read')).data?.data.length,
    queues: useQueues(readable('queue:read')).data?.data.length,
    ivrs: useIvrs(readable('flow:read')).data?.data.length,
    numbers: useNumbers(readable('number:read')).data?.data.length,
    // The customer-safe list: the staff one is refused to a customer.
    trunks: useTrunkStatus(readable('connectivity:read') || null).data?.data.length,
    users: useTenantUsers(readable('user:read')).data?.data.length,
  };

  const groups = useMemo(
    () => CONFIG_NAV
      .map((g) => ({ ...g, items: g.items.filter((i) => !i.permission || can(i.permission)) }))
      .filter((g) => g.items.length > 0),
    [can],
  );

  const active = groups.flatMap((g) => g.items).find((i) => i.key === section);

  const toggleGroup = (key: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });

  /** Switches to the extensions list AND names the seat, in one navigation. */
  // By id: Extensions rewrites it to the seat's number once it knows it.
  const openExtension = (id: string) => route.go(['extensions', id]);

  const go = (key: string) => {
    // `replace` so clicking through eight config sections does not bury the
    // page the operator arrived from under eight history entries.
    route.go(key === 'dashboard' ? [] : [key], { replace: true });
    const g = groupFor(key);
    if (g) setOpen((prev) => new Set(prev).add(g));
  };

  /**
   * A slug that matches no account, said plainly.
   *
   * Without this the page waits on a query that is disabled and never resolves
   * -- a spinner that means "that link is wrong" and does not say so. A mistyped
   * or renamed slug is the obvious cost of readable URLs, so it gets an answer.
   */
  if (unknownSlug) {
    return (
      <div className="arc-content">
        <div className="arc-page-max" style={{ paddingTop: 48 }}>
          <Empty icon="accounts" title={`No account called “${ref}”`}
            hint="The link may be old, or the account's short name may have changed. Open it from Customer accounts instead." />
        </div>
      </div>
    );
  }

  return (
    <div className="arc-app">
      <aside className="arc-sidebar" style={{ ['--arc-sidebar-w' as string]: '244px' }}>
        <div className="arc-brand">
          <BrandMark size={22} />
          <div className="arc-stack">
            <span className="arc-truncate" style={{ maxWidth: 160 }}>{t?.name ?? '\u2026'}</span>
            <span className="arc-brand-sub">Configuration</span>
          </div>
        </div>

        <nav className="arc-nav" aria-label="Account configuration">
          {groups.map((g) => {
            const isOpen = open.has(g.key);
            const hasActive = g.items.some((i) => i.key === section);
            return (
              <div key={g.key}>
                <button
                  type="button"
                  className="arc-nav-item arc-nav-group"
                  onClick={() => toggleGroup(g.key)}
                  aria-expanded={isOpen}
                  style={{
                    width: '100%', border: 'none', background: 'none', font: 'inherit',
                    color: hasActive && !isOpen ? 'var(--arc-nav-fg-strong)' : undefined,
                  }}
                >
                  <Icon name={g.icon} size={18} />
                  <span style={{ flex: 1, textAlign: 'left' }}>{g.label}</span>
                  <Icon name={isOpen ? 'chevronDown' : 'chevronRight'} size={12} />
                </button>

                {isOpen && (
                  <div className="arc-nav-sublist">
                    {g.items.map((i) => (
                      <button
                        key={i.key}
                        type="button"
                        className="arc-nav-item arc-nav-sub"
                        data-active={section === i.key}
                        onClick={() => go(i.key)}
                      >
                        <span style={{ flex: 1, textAlign: 'left' }}>{i.label}</span>
                        {i.countKey && counts[i.countKey] !== undefined && (
                          <span className="arc-nav-count arc-num">{counts[i.countKey]}</span>
                        )}
                        {/* Planned sections are marked, not hidden — the shape
                            of the product is worth showing, silently missing
                            menu entries are not. */}
                        {i.phase && <span className="arc-nav-soon">soon</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </aside>

      <div className="arc-main">
        <header className="arc-header">
          <div className="arc-row" style={{ gap: 9 }}>
            <span style={{ fontWeight: 600 }}>{t?.name ?? '\u2026'}</span>
            {/* Only when it is NOT active: a pill on every page view is a pill nobody
                reads, and it makes a suspended account look like more of the same
                (owner, 20 September 2026). Same rule the seat header follows. */}
            {t && t.status !== 'active' && <StatusBadge status={t.status} />}
            <span className="arc-mono arc-subtle">{t?.sip_domain}</span>
          </div>
          <div className="arc-spacer" />
          <span className="arc-subtle" style={{ fontSize: 12 }}>
            {active?.label}
          </span>
          <ThemeToggle />
        </header>

        <main className="arc-content">
          <div className="arc-page-max">
            {account.error && <ErrorBanner error={account.error} />}
            {/* Below the section is the section's own: the seat, and which of
                its settings is open, under Extensions. */}
            <SubrouteProvider value={childSubroute(route, [section])}>
              <Section section={section} tenantId={tenantId} tenant={t ?? null}
                onNavigate={go} onOpenExtension={openExtension} />
            </SubrouteProvider>
          </div>
        </main>
      </div>
    </div>
  );
}

function Section({ section, tenantId, tenant, onNavigate, onOpenExtension }: {
  section: string; tenantId: string; tenant: import('@arcnet/contracts').Tenant | null;
  onNavigate: (key: string) => void;
  onOpenExtension: (id: string) => void;
}) {
  const can = useSession((s) => s.can);
  /**
   * The menu hides a section the person may not use (config-nav.ts); this refuses it too, so a
   * typed address or a dashboard tile cannot open a screen whose data the API will refuse
   * (permission sweep, 19 September 2026).
   */
  const item = CONFIG_NAV.flatMap((g) => g.items).find((i) => i.key === section);
  if (item?.permission && !can(item.permission)) {
    return <Empty icon="info" title="Not available to you"
      hint="Your role does not include this. Ask an administrator on the account if you need it." />;
  }
  switch (section) {
    case 'dashboard':
      return <AccountDashboard tenantId={tenantId} tenant={tenant} onNavigate={onNavigate} />;
    case 'extensions':   return <ExtensionsPage tenantId={tenantId} embedded />;
    case 'portal-users': return <UsersTab tenantId={tenantId} />;
    case 'ring-groups':  return <RingGroupsTab tenantId={tenantId} />;
    case 'queues':       return <QueuesTab tenantId={tenantId} />;
    case 'conference':   return <ConferenceRoomsTab tenantId={tenantId} />;
    case 'parking':      return <ParkingLotsTab tenantId={tenantId} />;
    case 'ivrs':         return <IvrsTab tenantId={tenantId} />;
    case 'schedules':   return <SchedulesTab tenantId={tenantId} />;
    // Handsets are set up on each extension (Phone setup); links to the old list land here.
    case 'devices':     return <ProvisioningTab tenantId={tenantId} onNavigate={onNavigate} />;
    case 'departments': return <DepartmentsTab tenantId={tenantId} />;
    case 'provisioning': return <ProvisioningTab tenantId={tenantId} onNavigate={onNavigate} />;
    case 'templates':   return <TemplatesPage tenantId={tenantId} />;
    case 'recording':   return <RecordingTab tenantId={tenantId} />;
    case 'restrictions':
    case 'dial-permissions': return <DialPolicyTab tenantId={tenantId} />;
    case 'outbound':    return <OutboundRoutesTab tenantId={tenantId} />;
    // Folded into Hours & schedules (docs/26 E3); links to the old screen land there.
    case 'hours':       return <SchedulesTab tenantId={tenantId} />;
    case 'screening':   return <ScreeningTab tenantId={tenantId} />;
    case 'feature-codes': return <FeatureCodesTab tenantId={tenantId} />;
    case 'contacts':    return <ContactsPage tenantId={tenantId} />;
    case 'companies':   return <CompaniesPage tenantId={tenantId} />;
    case 'agents':      return <AgentsTab tenantId={tenantId} />;
    case 'usage':       return <UsageTab tenantId={tenantId} />;
    case 'api-keys':    return <ApiKeysTab tenantId={tenantId} />;
    case 'audit':       return <AuditTab tenantId={tenantId} />;
    case 'voicemail':   return <VoicemailTab tenantId={tenantId} onOpenExtension={onOpenExtension} />;
    case 'prompts':
    case 'media':       return <MediaTab key="prompts" tenantId={tenantId} section="prompts" />;
    case 'moh':         return <MediaTab key="moh" tenantId={tenantId} section="moh" />;
    case 'inbound':      return <InboundRoutesTab tenantId={tenantId} />;
    case 'numbers':      return <NumbersTab tenantId={tenantId} onNavigate={onNavigate} />;
    // Staff get the full view -- hosts, links, the switch's exact readings, and
    // the ArcNet/own-trunk switch. A customer on OUR trunks sees which carrier
    // carries their calls and whether it is up; one bringing its own trunk
    // sets that trunk up here.
    case 'trunks':       return can('trunk:read')
      ? <AccountTrunksTab tenantId={tenantId} />
      : tenant?.trunk_mode === 'byot'
        ? <OwnTrunksPanel tenantId={tenantId} />
        : <TrunkStatusTab tenantId={tenantId} />;
    case 'calls':        return <CdrsPage tenantId={tenantId} embedded />;
    case 'queue-reports': return <QueueReportTab tenantId={tenantId} />;
    case 'billing':      return <BillingPage tenantId={tenantId} embedded />;
    case 'profile':      return <ProfileSection tenantId={tenantId} tenant={tenant} />;
    default:             return <NotBuilt section={section} />;
  }
}

function ProfileSection({ tenantId, tenant }: {
  tenantId: string; tenant: import('@arcnet/contracts').Tenant | null;
}) {
  const can = useSession((s) => s.can);
  const stats = useCdrStats(can('cdr:read') ? tenantId : null);
  const extensions = useExtensions(can('extension:read') ? tenantId : null, { limit: 200 });
  const used = extensions.data?.data.length ?? 0;
  const limit = tenant?.max_extensions ?? 0;

  return (
    <>
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">Company profile</h1>
          <p className="arc-muted" style={{ marginTop: 3 }}>
            Account identity, limits and usage.
          </p>
        </div>
      </div>

      <div className="arc-grid arc-grid-4" style={{ marginBottom: 14 }}>
        <Stat label="Extensions" value={`${used}${limit ? ` / ${limit}` : ''}`}
          sub={limit && used >= limit ? 'at the limit' : 'in use'}
          tone={limit && used >= limit ? 'warn' : undefined} />
        <Stat label="Calls (30 days)" value={formatNumber(stats.data?.total_calls ?? 0)}
          sub={stats.data ? `${stats.data.answer_rate_pct}% answered` : undefined} />
        <Stat label="Spend (30 days)"
          value={formatMoney(stats.data?.spend ?? null, stats.data?.currency ?? 'ZAR')} />
        <Stat label="Channels" value={tenant?.max_concurrent_calls ?? '\u2014'}
          sub={`${tenant?.max_cps ?? '\u2014'} calls/sec`} />
      </div>

      <div className="arc-card">
        <div className="arc-card-head"><h2 className="arc-h2">Details</h2></div>
        <div className="arc-card-body">
          <dl className="arc-kv">
            <dt>Company name</dt><dd>{tenant?.name ?? '\u2014'}</dd>
            <dt>Slug</dt><dd className="arc-mono">{tenant?.slug ?? '\u2014'}</dd>
            <dt>SIP domain</dt>
            <dd className="arc-mono">
              {tenant?.sip_domain ?? '\u2014'}
              {/* ADR-002/013: HA1 credentials are bound to this realm. */}
              <span className="arc-subtle" style={{ marginLeft: 8 }}>permanent</span>
            </dd>
            <dt>Timezone</dt><dd>{tenant?.timezone ?? '\u2014'}</dd>
            <dt>Country</dt><dd>{tenant?.country_code ?? '\u2014'}</dd>
            <dt>Currency</dt><dd>{tenant?.currency ?? '\u2014'}</dd>
            <dt>Created</dt>
            <dd>{tenant ? new Date(tenant.created_at).toLocaleDateString('en-ZA') : '\u2014'}</dd>
          </dl>
        </div>
      </div>
    </>
  );
}

function NotBuilt({ section }: { section: string }) {
  const item = CONFIG_NAV.flatMap((g) => g.items).find((i) => i.key === section);
  return (
    <>
      <div className="arc-page-head">
        <div><h1 className="arc-h1">{item?.label ?? section}</h1></div>
      </div>
      <div className="arc-card">
        <Empty
          icon="settings"
          title={`${item?.label ?? section} is not built yet`}
          hint={`Planned for ${item?.phase ?? 'a later phase'}. The entry is here so the shape of the product is visible — there is no backend behind it yet.`}
        />
      </div>
    </>
  );
}
